import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useClerk, useSession } from "@clerk/react";
import { BodyCopy } from "../components/BodyCopy";
import { Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { useConsumerIdentityStore } from "../store/useConsumerIdentityStore";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "./useRouteWithToken";
import { bindConsumerIdentity, getWidgetApiError } from "../services/api";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";
import { getClerkConsumerPublishableKey } from "../auth/clerkPublishableKey";
import {
  applyConsumerIdentityBind,
  clearConsumerIdentityBind,
  consumerIdentityCacheMatches,
} from "../auth/consumerIdentityCache";
import { bindingMessageKey } from "../auth/authMode";
import { NovuInboxProvider } from "../components/notifications/NovuInboxProvider";
import {
  authPagePath,
  BIND_SUBJECT_BLOCKED_CODE,
  buildAuthPageUrl,
  serializeAuthResume,
  subjectBlockedTerminalState,
  type AuthResume,
} from "../auth/resume";
import {
  currentPathForNext,
  isTermsPending,
  welcomePagePath,
} from "../utils/termsGate";

const AUTH_RESET_PARAM = "authReset";

export interface RequireActiveClerkSessionProps {
  resume: AuthResume;
  /** Class C: occupancy must be this viewer. Other occupant → Unavailable. */
  requireOccupant?: boolean;
  children: ReactNode;
}

/**
 * Class B/C gate: active Consumer Clerk session, then idempotent bind.
 * Missing provider → /auth-page (shows missing-key copy). Pending → signOut recovery.
 * Bind result is cached in memory for (session.id, linkId) so route changes
 * do not re-show the binding shell.
 */
export function RequireActiveClerkSession({
  resume,
  requireOccupant = false,
  children,
}: RequireActiveClerkSessionProps) {
  if (!getClerkConsumerPublishableKey()) {
    return <RedirectToAuth resume={resume} />;
  }
  return (
    <RequireActiveClerkSessionInner
      resume={resume}
      requireOccupant={requireOccupant}
    >
      {children}
    </RequireActiveClerkSessionInner>
  );
}

/** Hook form of the same gate (call only under ClerkProvider). */
export function useRequireActiveClerkSession(options: {
  resume: AuthResume;
  requireOccupant?: boolean;
}): { allowed: boolean } {
  const gate = useClerkSessionGate(options);
  return { allowed: gate.allowed };
}

function RedirectToAuth({ resume }: { resume: AuthResume }) {
  const { redirectTo } = useRouteWithToken();
  const resumeKey = serializeAuthResume(resume);
  useEffect(() => {
    redirectTo(authPagePath(resume), { replace: true });
  }, [redirectTo, resumeKey]);
  return <AuthBindingShell />;
}

function RequireActiveClerkSessionInner({
  resume,
  requireOccupant,
  children,
}: RequireActiveClerkSessionProps) {
  const gate = useClerkSessionGate({ resume, requireOccupant });
  if (!gate.allowed) {
    return <AuthBindingShell message={gate.message} />;
  }
  return <NovuInboxProvider>{children}</NovuInboxProvider>;
}

function useClerkSessionGate({
  resume,
  requireOccupant = false,
}: {
  resume: AuthResume;
  requireOccupant?: boolean;
}): { allowed: boolean; message?: string } {
  const { t } = useTranslation();
  const clerk = useClerk();
  const { isLoaded, session: clerkSession } = useSession();
  const { redirectTo, linkId } = useRouteWithToken();
  const cachedBind = useConsumerIdentityStore((s) => s.bind);
  const sessionData = useSessionStore((s) => s.session.data);
  const [error, setError] = useState<string | null>(null);
  const resetPendingRef = useRef(false);
  const bindSignOutRef = useRef(false);
  const retryBindOnOnlineRef = useRef(false);
  const [bindAttempt, setBindAttempt] = useState(0);
  const redirectToRef = useRef(redirectTo);
  redirectToRef.current = redirectTo;

  const sessionStatus = clerkSession?.status;
  const clerkSessionId = clerkSession?.id;
  const resumeKey = serializeAuthResume(resume);
  const cacheHit = consumerIdentityCacheMatches(
    cachedBind,
    clerkSessionId,
    linkId,
  );

  useEffect(() => {
    if (!isLoaded) return;
    if (sessionStatus === "active") return;
    // Clerk's signOut redirect carries authError=bind; do not race it.
    if (bindSignOutRef.current) return;
    if (sessionStatus === "pending") {
      if (resetPendingRef.current) return;
      resetPendingRef.current = true;
      clearConsumerIdentityBind();
      const params = new URLSearchParams();
      if (linkId) params.set("linkId", linkId);
      params.set("resume", resumeKey);
      params.set(AUTH_RESET_PARAM, "1");
      const redirectUrl = `${window.location.origin}${APP_ROUTES.AUTH_PAGE}?${params.toString()}`;
      void clerk.signOut({ redirectUrl }).catch(() => {
        redirectToRef.current(authPagePath(resume), { replace: true });
      });
      return;
    }
    redirectToRef.current(authPagePath(resume), { replace: true });
  }, [isLoaded, sessionStatus, clerk, linkId, resume, resumeKey]);

  useEffect(() => {
    if (!cacheHit || !cachedBind) return;
    if (requireOccupant && cachedBind.occupancy === "other_occupant") {
      redirectToRef.current(APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE, {
        replace: true,
      });
    }
  }, [cacheHit, cachedBind, requireOccupant]);

  const termsPending = isTermsPending(sessionData);
  const skipWelcome =
    requireOccupant && cachedBind?.occupancy === "other_occupant";

  useEffect(() => {
    const onOnline = () => {
      if (!retryBindOnOnlineRef.current) return;
      retryBindOnOnlineRef.current = false;
      setBindAttempt((n) => n + 1);
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // Signed-in routes are behind the agreement: reaching one with the consent
  // still pending (deep link, reload) detours through Welcome. Unavailable
  // (`other_occupant`) must not be wrapped.
  useEffect(() => {
    if (!cacheHit || !termsPending || skipWelcome) return;
    redirectToRef.current(welcomePagePath(currentPathForNext()), {
      replace: true,
    });
  }, [cacheHit, termsPending, skipWelcome]);

  useEffect(() => {
    if (!isLoaded || sessionStatus !== "active" || !clerkSessionId) return;
    if (cacheHit) return;

    let cancelled = false;

    void (async () => {
      try {
        const session = clerkSession;
        if (!session) return;
        const token = await session.getToken();
        if (cancelled) return;
        if (!token) throw new Error("missing_token");
        const bound = await bindConsumerIdentity(token);
        if (cancelled) return;

        applyConsumerIdentityBind({
          clerkSessionId,
          linkId,
          bound,
        });
        if (cancelled) return;

        if (requireOccupant && bound.occupancy === "other_occupant") {
          redirectToRef.current(APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE, {
            replace: true,
          });
        }
      } catch (bindError) {
        console.error("[RequireActiveClerkSession] bind failed:", bindError);
        if (cancelled) return;
        if (isAxiosNetworkFailure(bindError)) {
          retryBindOnOnlineRef.current = true;
          return;
        }
        if (getWidgetApiError(bindError)?.code === BIND_SUBJECT_BLOCKED_CODE) {
          redirectToRef.current(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: subjectBlockedTerminalState(t),
          });
          return;
        }
        // An active session with a failed bind cannot enter guarded routes;
        // drop it so /auth-page shows the form with the bind error.
        bindSignOutRef.current = true;
        clearConsumerIdentityBind();
        const redirectUrl = buildAuthPageUrl({
          origin: window.location.origin,
          linkId,
          resumeRaw: resumeKey,
          bindError: true,
        });
        try {
          await clerk.signOut({ redirectUrl });
        } catch (signOutError) {
          console.error("[RequireActiveClerkSession] signOut failed:", signOutError);
          bindSignOutRef.current = false;
          if (!cancelled && isAxiosNetworkFailure(signOutError)) {
            retryBindOnOnlineRef.current = true;
            return;
          }
          if (!cancelled) setError(t("auth.errorBind"));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Bind once per Clerk session id + link until the in-memory cache hits.
    // Do not depend on `redirectTo` / `session.data` / the Clerk session object:
    // those change every render (or after bind) and would cancel a 200 bind.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clerkSession captured for getToken
  }, [isLoaded, sessionStatus, clerkSessionId, linkId, cacheHit, bindAttempt, requireOccupant, t]);

  if (error) return { allowed: false, message: error };
  if (!isLoaded || sessionStatus !== "active") {
    return { allowed: false };
  }
  if (!cacheHit || skipWelcome || termsPending) {
    return { allowed: false };
  }
  return { allowed: true };
}

const AuthBindingShell = memo(({ message }: { message?: string }) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const copy = message || t(bindingMessageKey());
  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Content>
        <div className="landing-section content-center loader-page">
          <section className="content">
            {!message && (
              <div
                className="loader-spinner"
                role="status"
                aria-label={copy}
              />
            )}
            <BodyCopy textColor={colorConfig.title}>{copy}</BodyCopy>
          </section>
        </div>
      </Layout.Content>
    </AuraLayout>
  );
});
